import { useState, useEffect } from 'react';
import { api } from '../api';
import { useData } from '../hooks/useData';

const BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001';

export default function ActionsPage({ onSelectMeeting, refreshTrigger }) {
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('open');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [updating, setUpdating] = useState(null);

  const { actions: allActions, loading: dataLoading } = useData(refreshTrigger);
  useEffect(() => {
    setActions(allActions);
    setLoading(dataLoading);
  }, [allActions, dataLoading]);

  const toggle = async (item) => {
    const next = item.status === 'done' ? 'open' : 'done';
    setUpdating(item.id);
    try {
      await api.updateActionItem(item.meeting_id, item.id, { status: next });
      setActions(prev => prev.map(a => a.id === item.id ? { ...a, status: next } : a));
    } finally { setUpdating(null); }
  };

  const open = actions.filter(a => a.status !== 'done');
  const done = actions.filter(a => a.status === 'done');
  const overdue = open.filter(a => {
    if (!a.deadline) return false;
    const d = new Date(a.deadline);
    return !isNaN(d) && d < new Date();
  });

  const filtered = actions.filter(a => {
    if (statusFilter === 'open' && a.status === 'done') return false;
    if (statusFilter === 'done' && a.status !== 'done') return false;
    if (priorityFilter !== 'all' && a.priority !== priorityFilter) return false;
    return true;
  });

  const debtScore = actions.length === 0 ? 100 : Math.max(0, Math.round((done.length / actions.length) * 100));

  return (
    <div className="page fade-in">
      <div style={{ marginBottom: '1.75rem' }}>
        <div style={{ fontSize: 24, fontWeight: 700, letterSpacing: '-0.02em', marginBottom: 4 }}>Action Items</div>
        <div style={{ fontSize: 13, color: 'var(--text-2)' }}>All tasks across every meeting in one place</div>
      </div>

      {/* Stats */}
      <div className="grid-stats">
        {[
          { val: open.length, label: 'Open', color: 'var(--accent)', sub: 'tasks remaining' },
          { val: overdue.length, label: 'Overdue', color: overdue.length > 0 ? 'var(--red)' : 'var(--text-3)', sub: overdue.length > 0 ? 'needs attention' : 'none overdue' },
          { val: done.length, label: 'Completed', color: 'var(--text-3)', sub: 'tasks done' },
          { val: `${debtScore}%`, label: 'Completion Rate', color: debtScore >= 80 ? 'var(--accent)' : debtScore >= 50 ? 'var(--yellow)' : 'var(--red)', sub: debtScore >= 80 ? 'great progress' : 'keep going' },
        ].map(s => (
          <div key={s.label} style={{ background: 'var(--bg-2)', border: '1px solid var(--border)', borderRadius: 10, padding: '1rem 1.25rem' }}>
            <div style={{ fontFamily: 'var(--mono)', fontSize: 26, fontWeight: 700, color: s.color, lineHeight: 1, marginBottom: 4 }}>{s.val}</div>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-2)', marginBottom: 2 }}>{s.label}</div>
            <div style={{ fontSize: 11, color: 'var(--text-3)' }}>{s.sub}</div>
          </div>
        ))}
      </div>

      {/* Overdue banner */}
      {overdue.length > 0 && (
        <div style={{ background: 'var(--red-dim)', border: '1px solid rgba(255,68,85,0.25)', borderRadius: 8, padding: '0.75rem 1rem', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <span style={{ fontSize: 16 }}>⚠</span>
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--red)', marginBottom: 2 }}>{overdue.length} overdue task{overdue.length > 1 ? 's' : ''}</div>
            <div style={{ fontSize: 12, color: 'var(--text-3)' }}>{overdue.slice(0,2).map(a => a.description).join(' · ')}{overdue.length > 2 ? ` +${overdue.length-2} more` : ''}</div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ display: 'flex', background: 'var(--bg-2)', border: '1px solid var(--border)', borderRadius: 8, padding: 3 }}>
          {['open','done','all'].map(f => (
            <button key={f} onClick={() => setStatusFilter(f)} style={{
              padding: '4px 14px', borderRadius: 5, border: 'none', cursor: 'pointer',
              fontSize: 12, fontWeight: 500, transition: 'all 0.15s',
              background: statusFilter === f ? 'var(--bg-4)' : 'transparent',
              color: statusFilter === f ? 'var(--text)' : 'var(--text-3)',
            }}>{f}</button>
          ))}
        </div>
        <div style={{ display: 'flex', background: 'var(--bg-2)', border: '1px solid var(--border)', borderRadius: 8, padding: 3 }}>
          {['all','high','medium','low'].map(p => (
            <button key={p} onClick={() => setPriorityFilter(p)} style={{
              padding: '4px 12px', borderRadius: 5, border: 'none', cursor: 'pointer',
              fontSize: 12, fontWeight: 500, transition: 'all 0.15s',
              background: priorityFilter === p ? 'var(--bg-4)' : 'transparent',
              color: priorityFilter === p ? 'var(--text)' : 'var(--text-3)',
            }}>{p}</button>
          ))}
        </div>
        <span style={{ fontSize: 12, color: 'var(--text-3)', marginLeft: 4 }}>{filtered.length} result{filtered.length !== 1 ? 's' : ''}</span>
      </div>

      {/* Table */}
      {loading ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {[1,2,3,4,5].map(i => <div key={i} className="skeleton" style={{ height: 52, borderRadius: 8 }} />)}
        </div>
      ) : filtered.length === 0 ? (
        <div style={{ background: 'var(--bg-2)', border: '1px solid var(--border)', borderRadius: 12, padding: '4rem 2rem', textAlign: 'center' }}>
          <div style={{ fontSize: 40, marginBottom: '1rem' }}>✅</div>
          <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-2)', marginBottom: 6 }}>
            {statusFilter === 'open' ? "You're all caught up!" : 'No items found'}
          </div>
          <div style={{ fontSize: 13, color: 'var(--text-3)' }}>
            {statusFilter === 'open' ? 'No open action items right now.' : 'Try adjusting your filters.'}
          </div>
        </div>
      ) : (
        <div style={{ background: 'var(--bg-2)', border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden' }}>
          <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th style={{ width: 40 }}></th>
                <th>Task</th>
                <th style={{ width: 120 }}>Owner</th>
                <th style={{ width: 120 }}>Deadline</th>
                <th style={{ width: 90 }}>Priority</th>
                <th style={{ width: 140 }}>Meeting</th>
                <th style={{ width: 90 }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(item => {
                const isOverdue = item.status !== 'done' && item.deadline && !isNaN(new Date(item.deadline)) && new Date(item.deadline) < new Date();
                return (
                  <tr key={item.id} style={{ opacity: item.status === 'done' ? 0.45 : 1 }}>
                    <td>
                      <button onClick={() => toggle(item)} disabled={updating === item.id}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: item.status === 'done' ? 'var(--accent)' : 'var(--text-3)', fontSize: 16, padding: '2px 6px' }}>
                        {item.status === 'done' ? '✓' : '○'}
                      </button>
                    </td>
                    <td style={{ textDecoration: item.status === 'done' ? 'line-through' : 'none', color: item.status === 'done' ? 'var(--text-3)' : 'var(--text)' }}>
                      {item.description}
                    </td>
                    <td style={{ color: 'var(--text-2)', fontSize: 12 }}>{item.owner ?? '—'}</td>
                    <td style={{ fontFamily: 'var(--mono)', fontSize: 11, color: isOverdue ? 'var(--red)' : 'var(--text-3)' }}>
                      {item.deadline ?? '—'}{isOverdue ? ' ⚠' : ''}
                    </td>
                    <td><span className={`badge badge-${item.priority}`}>{item.priority}</span></td>
                    <td>
                      <span onClick={() => onSelectMeeting(item.meeting_id)}
                        style={{ fontSize: 11, color: 'var(--text-3)', cursor: 'pointer', textDecoration: 'underline', textDecorationStyle: 'dotted' }}>
                        {item.meeting_title}
                      </span>
                    </td>
                    <td><span className={`badge badge-${item.status === 'done' ? 'done-item' : 'open'}`}>{item.status}</span></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        </div>
      )}
    </div>
  );
}
